
import React from 'react';
import { SystemConfig } from '@/shared/types';
import { Globe, Database, Image as ImageIcon, Cloud, DollarSign, Upload, Trash2, Video } from 'lucide-react';

interface ConfigFormProps {
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
}

export const AppConfigForm: React.FC<ConfigFormProps> = ({ config, setConfig }) => {
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setConfig({ ...config, platformLogoUrl: result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="bg-white p-5 rounded border border-gray-200">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        <div className="p-1.5 bg-gray-100 text-gray-600 rounded"><Globe size={16}/></div>
        <div>
          <h3 className="font-semibold text-gray-900 text-sm">App Settings</h3>
          <p className="text-xs text-gray-500">General application parameters</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">App Name</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none text-sm bg-white text-gray-800 transition-colors"
            value={config.appName}
            onChange={e => setConfig({...config, appName: e.target.value})}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">App URL</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
            value={config.appUrl}
            onChange={e => setConfig({...config, appUrl: e.target.value})}
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Demo Video URL (Cloudinary/MP4)</label>
          <div className="relative">
            <Video size={14} className="absolute left-3 top-2.5 text-gray-400"/>
            <input
              type="text"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
              value={config.demoVideoUrl || ''}
              placeholder="https://res.cloudinary.com/..."
              onChange={e => setConfig({...config, demoVideoUrl: e.target.value})}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">Direct URL to a .mp4 video file for the landing page demo</p>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1.5">Platform Branding (Logo)</label>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-50 border border-gray-200 rounded flex items-center justify-center overflow-hidden relative group">
              {config.platformLogoUrl ? (
                <>
                  <img src={config.platformLogoUrl} alt="System Logo" className="w-full h-full object-contain p-2"/>
                  <button
                    type="button"
                    onClick={() => setConfig({...config, platformLogoUrl: ''})}
                    className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                  >
                    <Trash2 size={14}/>
                  </button>
                </>
              ) : (
                <span className="text-gray-300 font-medium text-xs">No Logo</span>
              )}
            </div>
            <label className="cursor-pointer px-3 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2">
              <Upload size={14}/> Upload Logo
              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </label>
          </div>
          <p className="text-xs text-gray-400 mt-1">Logo appears on landing page, login screen, and dashboard header</p>
        </div>
        <div className="flex items-center gap-2 pt-2">
          <input
            type="checkbox"
            id="maintenance"
            className="w-4 h-4 accent-gray-900 bg-white border-gray-300 rounded cursor-pointer focus:ring-2 focus:ring-gray-500/50"
            checked={config.maintenanceMode}
            onChange={e => setConfig({...config, maintenanceMode: e.target.checked})}
          />
          <label htmlFor="maintenance" className="text-sm font-medium text-gray-700 cursor-pointer">Enable Maintenance Mode</label>
        </div>
      </div>
    </div>
  );
};

export const DatabaseConfigForm: React.FC<ConfigFormProps> = ({ config, setConfig }) => (
  <div className="bg-white p-5 rounded border border-gray-200">
    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
      <div className="p-1.5 bg-emerald-100 text-emerald-700 rounded"><Database size={16}/></div>
      <div>
        <h3 className="font-semibold text-gray-900 text-sm">Database Configuration</h3>
        <p className="text-xs text-gray-500">Connection strings and service keys</p>
      </div>
    </div>
    <div className="grid md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Project URL</label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
          value={config.supabaseUrl}
          onChange={e => setConfig({...config, supabaseUrl: e.target.value})}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Anon (Public) Key</label>
        <input
          type="password"
          className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
          value={config.supabaseAnonKey}
          onChange={e => setConfig({...config, supabaseAnonKey: e.target.value})}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Service Role Key</label>
        <input
          type="password"
          className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
          value={config.supabaseServiceRoleKey}
          onChange={e => setConfig({...config, supabaseServiceRoleKey: e.target.value})}
        />
      </div>
    </div>
  </div>
);

export const PaymentConfigForm: React.FC<ConfigFormProps> = ({ config, setConfig }) => (
  <div className="bg-white p-5 rounded border border-gray-200">
    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
      <div className="p-1.5 bg-indigo-100 text-indigo-700 rounded"><DollarSign size={16}/></div>
      <div>
        <h3 className="font-semibold text-gray-900 text-sm">Payment Gateways</h3>
        <p className="text-xs text-gray-500">Configure PayPal, M-Pesa</p>
      </div>
    </div>

    {/* PayPal */}
    <div className="mb-5">
      <h4 className="font-semibold text-gray-900 text-sm mb-3 border-l-2 border-blue-500 pl-2">PayPal</h4>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Client ID</label>
          <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
          value={config.paypalClientId} onChange={e => setConfig({...config, paypalClientId: e.target.value})} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Client Secret</label>
          <input type="password" className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
          value={config.paypalClientSecret} onChange={e => setConfig({...config, paypalClientSecret: e.target.value})} />
        </div>
      </div>
    </div>

    {/* M-Pesa */}
    <div>
      <h4 className="font-semibold text-gray-900 text-sm mb-3 border-l-2 border-emerald-500 pl-2">M-Pesa (Safaricom)</h4>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Consumer Key</label>
          <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
          value={config.mpesaConsumerKey} onChange={e => setConfig({...config, mpesaConsumerKey: e.target.value})} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Consumer Secret</label>
          <input type="password" className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
          value={config.mpesaConsumerSecret} onChange={e => setConfig({...config, mpesaConsumerSecret: e.target.value})} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Shortcode</label>
          <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded focus:border-gray-400 focus:outline-none font-mono text-xs bg-white text-gray-800 transition-colors"
          value={config.mpesaShortcode} onChange={e => setConfig({...config, mpesaShortcode: e.target.value})} />
        </div>
      </div>
    </div>
  </div>
);
